import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, ClampText, DataTable, EmptyValue, HiddenMatchHint, ListPage, PageHeader } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import ServerPagination from '../../../components/ServerPagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import {
  PO_TYPE_LABELS,
  PurchaseOrder,
  PurchaseOrderLineItem,
  PurchaseOrderQueryParams,
  purchaseOrderService,
} from '../../../services/purchaseOrderService';
import ConsumptionBar from '../components/ConsumptionBar';
import PoConsumptionDetailModal from '../components/PoConsumptionDetailModal';
import PurchaseOrdersFilters, {
  EMPTY_PO_FILTERS,
  PurchaseOrderFilterState,
  poFiltersChanged,
  poStatusesFor,
} from '../components/PurchaseOrdersFilters';
import { currency, shortDate } from '../../vendors/utils/format';

/** sessionStorage page key for this page's remembered list state (V2-024). */
const LIST_KEY = 'purchaseOrders';

/** Expander column width; the pinned PO Number column is offset by the same amount. */
const EXPANDER_COL_WIDTH = '3rem';

const StatusBadge: React.FC<{ status: PurchaseOrder['status'] }> = ({ status }) => {
  if (status === 'ACTIVE') return <Badge bg="success">Active</Badge>;
  if (status === 'INACTIVE') return <Badge bg="secondary">Inactive</Badge>;
  return <Badge bg="warning">Reserved</Badge>;
};

/**
 * HE-P01, HE-P02, HE-P03 — the purchase order list that replaces the SharePoint sheet.
 *
 * Read-only apart from the status toggle (HE-P04), which is what the spreadsheet does today.
 * Purchase requisitions and procurement stay in TrueContext this phase.
 */
const PurchaseOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Applied filters, search, sort and page are kept for the sign-in (V2-024).
  const [filters, setFilters] = usePersistedListState<PurchaseOrderFilterState>(LIST_KEY, 'filters', EMPTY_PO_FILTERS);
  const [searchTerm, setSearchTerm] = usePersistedListState(LIST_KEY, 'search', '');
  const [searchInput, setSearchInput] = useState(searchTerm);
  // The same search without the Display Inactive filter, for the hidden-match hint (SRCH-05).
  const [unfilteredMatchCount, setUnfilteredMatchCount] = useState<number | null>(null);

  const [sort, setSort] = usePersistedListState<{ by: string | null; dir: 'asc' | 'desc' }>(LIST_KEY, 'sort', {
    by: null,
    dir: 'asc',
  });
  const sortBy = sort.by;
  const sortDir = sort.dir;

  /**
   * Expanded rows and their line items. Line items are NOT in the list payload — a PO can carry
   * dozens and the list would pay for them on every page load — so they are fetched on first
   * expand and kept. Collapsing keeps the cache: re-opening the same row must not refetch.
   */
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [lineItems, setLineItems] = useState<Map<number, PurchaseOrderLineItem[]>>(new Map());
  const [loadingLines, setLoadingLines] = useState<Set<number>>(new Set());
  const [consumptionPo, setConsumptionPo] = useState<PurchaseOrder | null>(null);

  const toggleExpanded = useCallback(
    async (po: PurchaseOrder) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(po.id)) next.delete(po.id);
        else next.add(po.id);
        return next;
      });

      if (lineItems.has(po.id) || loadingLines.has(po.id)) return;

      setLoadingLines((prev) => new Set(prev).add(po.id));
      try {
        const items = await purchaseOrderService.getLineItems(po.id);
        setLineItems((prev) => new Map(prev).set(po.id, items));
      } catch {
        // Cache the empty result rather than retrying on every toggle; the row says so.
        setLineItems((prev) => new Map(prev).set(po.id, []));
      } finally {
        setLoadingLines((prev) => {
          const next = new Set(prev);
          next.delete(po.id);
          return next;
        });
      }
    },
    [lineItems, loadingLines]
  );

  const [savedPage, setSavedPage] = usePersistedListState(LIST_KEY, 'page', { page: 1, limit: 20 });
  const [pagination, setPagination] = useState({
    ...savedPage,
    totalCount: 0,
    totalPages: 0,
  });
  useEffect(() => {
    setSavedPage({ page: pagination.page, limit: pagination.limit });
  }, [pagination.page, pagination.limit]);

  /** Request filters; Display Inactive off sends Reserved + Active, on sends no status. */
  const queryFilters = useCallback(
    (withStatus: boolean): PurchaseOrderQueryParams => {
      const statuses = withStatus ? poStatusesFor(filters.displayInactive) : undefined;
      return {
        ...(searchTerm && { search: searchTerm }),
        ...(filters.vendorIds.length && { vendorIds: filters.vendorIds }),
        ...(filters.types.length && { types: filters.types as PurchaseOrderQueryParams['types'] }),
        ...(filters.serviceAreaIds.length && { serviceAreaIds: filters.serviceAreaIds }),
        ...(filters.foremanAreaIds.length && { foremanAreaIds: filters.foremanAreaIds }),
        ...(statuses && { statuses }),
      };
    },
    [searchTerm, filters]
  );

  const fetchPurchaseOrders = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        setError(null);

        const params: PurchaseOrderQueryParams = {
          page,
          limit: pagination.limit,
          ...queryFilters(true),
          ...(sortBy && { sortBy, sortOrder: sortDir }),
        };

        const response = await purchaseOrderService.getPurchaseOrdersPaginated(params);
        setPurchaseOrders(response.data);
        setPagination((prev) => ({
          ...prev,
          page: response.pagination.page,
          totalCount: response.pagination.totalCount,
          totalPages: response.pagination.totalPages,
        }));
      } catch (err) {
        console.error('Error loading purchase orders:', err);
        setError(err instanceof Error ? err.message : 'Failed to load purchase orders');
        setPurchaseOrders([]);
        setPagination((prev) => ({ ...prev, totalCount: 0, totalPages: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, queryFilters, sortBy, sortDir]
  );

  // Filter, search and sort changes all reset to page 1: staying on page 7 of a result set
  // that now has two pages shows an empty table and reads as "no results". Arriving on the page
  // (or coming back to it) restores the remembered page instead.
  const queryKey = JSON.stringify([searchTerm, filters, sortBy, sortDir, pagination.limit]);
  const lastQueryKey = useRef<string | null>(null);
  useEffect(() => {
    const first = lastQueryKey.current === null;
    if (lastQueryKey.current === queryKey) return;
    lastQueryKey.current = queryKey;
    fetchPurchaseOrders(first ? savedPage.page : 1);
  }, [queryKey]);

  // Hidden-match hint (V2-076): the same search without the Display Inactive filter, one row,
  // reading the total.
  useEffect(() => {
    if (!searchTerm || filters.displayInactive) {
      setUnfilteredMatchCount(null);
      return;
    }
    let cancelled = false;
    purchaseOrderService
      .getPurchaseOrdersPaginated({ page: 1, limit: 1, ...queryFilters(false) })
      .then((res) => {
        if (!cancelled) setUnfilteredMatchCount(res.pagination.totalCount);
      })
      .catch(() => {
        if (!cancelled) setUnfilteredMatchCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [searchTerm, filters.displayInactive, queryFilters]);

  // The default (Display Inactive off) alone does not make an empty list "filtered".
  const hasFilters = !!searchTerm || poFiltersChanged(filters);

  const handleSearch = (value: string) => {
    setSearchInput(value.trim());
    setSearchTerm(value.trim());
  };

  const columns: DataTableColumn<PurchaseOrder>[] = [
    {
      key: 'expander',
      header: '',
      // Pinned with the PO number so the row's controls stay in reach while scrolling sideways.
      pin: 'left',
      width: EXPANDER_COL_WIDTH,
      mobile: 'hidden',
      cell: (po) => (
        <button
          type="button"
          aria-expanded={expanded.has(po.id)}
          aria-label={expanded.has(po.id) ? `Hide line items for ${po.poNumber}` : `Show line items for ${po.poNumber}`}
          className="tw-border-0 tw-bg-transparent tw-p-1 tw-text-content-muted hover:tw-text-content"
          onClick={(e) => {
            // The row itself navigates to the detail page; expanding must not.
            e.stopPropagation();
            void toggleExpanded(po);
          }}
        >
          <i className={`bi ${expanded.has(po.id) ? 'bi-chevron-down' : 'bi-chevron-right'}`} />
        </button>
      ),
    },
    {
      key: 'poNumber',
      header: 'PO Number',
      sortable: true,
      mobile: 'title',
      pin: 'left',
      // Offset by the expander column (the --wrap-pin-left contract in tailwind.css).
      className: 'tw-whitespace-nowrap [--wrap-pin-left:3rem]',
      headerClassName: '[--wrap-pin-left:3rem]',
      cell: (po) => (
        <button
          type="button"
          className="tw-text-primary tw-underline tw-bg-transparent tw-border-0 tw-p-0"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/purchase-orders/${po.id}`);
          }}
        >
          {po.poNumber}
        </button>
      ),
    },
    {
      /*
       * A purchase order whose work-instructions review was deferred still owes something, and
       * that is invisible on a list of numbers and amounts. It gets its own narrow column
       * rather than a suffix on the PO number so it survives the mobile card layout, where a
       * badge appended to the title would be truncated.
       *
       * Deliberately silent for "Yes" and "Not Applicable": a warning shown on every row is a
       * warning nobody reads.
       */
      key: 'workInstructions',
      header: '',
      className: 'tw-w-8',
      // Server-computed from the per-line answers, so the icon and the feed agree.
      cell: (po) =>
        po.workInstructionsOutstanding ? (
          <i
            className="bi bi-exclamation-triangle-fill tw-text-warning"
            title="At least one line still needs its work instructions reviewed"
            aria-label="Work instructions review outstanding on at least one line"
          />
        ) : null,
    },
    {
      key: 'description',
      header: 'Description',
      sortable: true,
      mobile: 'subtitle',
      headerClassName: 'tw-min-w-[18rem]',
      cell: (po) => (po.description ? <ClampText>{po.description}</ClampText> : <EmptyValue />),
    },
    {
      key: 'vendor',
      header: 'Vendor',
      sortable: true,
      cell: (po) => po.vendor?.name ?? <EmptyValue />,
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      cell: (po) => PO_TYPE_LABELS[po.type] ?? po.type,
    },
    {
      key: 'serviceArea',
      header: 'Service Area',
      sortable: true,
      cell: (po) => po.serviceArea?.name ?? <EmptyValue />,
    },
    {
      key: 'foremanArea',
      header: 'Foreman Area',
      sortable: true,
      cell: (po) => po.foremanArea?.name ?? <EmptyValue />,
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      align: 'right',
      cell: (po) => currency(po.amount),
    },
    {
      key: 'progress',
      header: 'Progress',
      // Not sortable: consumption is computed per page rather than stored, so the server has
      // nothing to order by. Offering the affordance would sort only the visible rows.
      mobile: 'meta',
      cell: (po) => (
        <ConsumptionBar
          consumption={po.consumption}
          compact
          onClick={() => setConsumptionPo(po)}
        />
      ),
    },
    {
      key: 'requestedByName',
      header: 'Requested By',
      sortable: true,
      cell: (po) =>
        po.requestedBy ? `${po.requestedBy.name} ${po.requestedBy.lastName}` : po.requestedByName || <EmptyValue />,
    },
    {
      key: 'requestDate',
      header: 'Requested',
      sortable: true,
      cell: (po) => shortDate(po.requestDate),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobile: 'trailing',
      cell: (po) => <StatusBadge status={po.status} />,
    },
  ];

  return (
    <ListPage className="purchase-orders-page">
      <PageHeader
        title="Purchase Orders"
      />

      {/* Pending vs applied: the filter panel only calls back on Apply / Clear All (W-M1). */}
      <PurchaseOrdersFilters
        filters={filters}
        onFilterChange={setFilters}
        onClearFilters={() => {
          setFilters(EMPTY_PO_FILTERS);
          setSearchInput('');
          setSearchTerm('');
        }}
        searchApplied={searchTerm !== ''}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: handleSearch,
          placeholder: 'PO number, description, vendor, or reference',
          label: 'Search purchase orders',
        }}
      />

      {unfilteredMatchCount !== null && !loading && (
        <HiddenMatchHint
          search={searchTerm}
          shownCount={pagination.totalCount}
          unfilteredCount={unfilteredMatchCount}
          shownNoun="active purchase orders"
          hiddenNoun={['inactive match', 'inactive matches']}
          onShow={() => setFilters((prev) => ({ ...prev, displayInactive: true }))}
        />
      )}

      {error && (
        <div className="tw-mb-3 tw-text-danger" role="alert">
          {error}
        </div>
      )}

      {/* DataTable fill: the one scroll box, sticky header, expander + PO Number pinned left. */}
      <ListPage.Body aria-busy={loading}>
      <DataTable
        fill
        columns={columns}
        data={purchaseOrders}
        rowKey={(po) => po.id}
        loading={loading}
        hover
        onRowClick={(po) => navigate(`/purchase-orders/${po.id}`)}
        isExpanded={(po) => expanded.has(po.id)}
        renderExpanded={(po) => {
          if (loadingLines.has(po.id)) {
            return <div className="tw-p-3 tw-text-sm tw-text-content-muted">Loading line items…</div>;
          }
          const items = lineItems.get(po.id) ?? [];
          if (items.length === 0) {
            return <div className="tw-p-3 tw-text-sm tw-text-content-muted">No line items on this purchase order.</div>;
          }
          return (
            <div className="tw-overflow-x-auto tw-p-3">
              <table className="tw-w-full tw-text-sm">
                <thead>
                  <tr className="tw-text-left tw-text-xs tw-font-semibold tw-text-content-muted">
                    <th className="tw-pb-1 tw-pr-4 tw-font-medium">Resource</th>
                    <th className="tw-pb-1 tw-pr-4 tw-font-medium">Task</th>
                    <th className="tw-pb-1 tw-pr-4 tw-font-medium tw-text-right">Quantity</th>
                    <th className="tw-pb-1 tw-font-medium">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((li) => (
                    <tr key={li.id} className="tw-border-t tw-border-line">
                      <td className="tw-py-1 tw-pr-4">{li.resource?.name ?? li.description ?? '–'}</td>
                      <td className="tw-py-1 tw-pr-4">
                        {li.task ? li.task.taskNumber ?? String(li.task.id) : '–'}
                      </td>
                      <td className="tw-py-1 tw-pr-4 tw-text-right tw-tabular-nums">{li.quantity}</td>
                      <td className="tw-py-1">{li.unit ?? li.resource?.unit ?? '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={(key, dir) => setSort({ by: key, dir })}
        emptyMessage={
          <div className="tw-py-2">
            <i className="bi bi-inbox tw-text-xl tw-block tw-mb-2" />
            {/*
              "match these filters" is only true when something IS filtered. With none applied
              it reads as though the user mis-filtered, when the table is simply empty.
            */}
            {hasFilters
              ? 'No purchase orders match these filters.'
              : 'No purchase orders yet. They arrive from the TrueContext import.'}
          </div>
        }
      />
      </ListPage.Body>

      {pagination.totalPages > 1 && (
        <ServerPagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          pageSize={pagination.limit}
          onPageChange={(page) => fetchPurchaseOrders(page)}
          onPageSizeChange={(size) => setPagination((prev) => ({ ...prev, limit: size, page: 1 }))}
          loading={loading}
          className="tw-mt-2"
        />
      )}

      <PoConsumptionDetailModal
        show={!!consumptionPo}
        po={consumptionPo}
        onHide={() => setConsumptionPo(null)}
      />
    </ListPage>
  );
};

export default PurchaseOrdersPage;
